import type {
  EstadoLinha,
  LinhaPlanejamentoControle,
  NaturezaLinha,
  TotaisPlanejamento,
} from "@/modules/financeiro-obras";
import {
  AGUARDANDO_APROVACAO,
  CANCELAMENTO_AGUARDANDO_APROVACAO_TIPO,
  EM_APROVACAO,
  ESCOPO_PENDENTE,
  NO_VALUE,
  SEM_ESCOPO_MACRO,
  GMUD_ESCOPO_BLOQUEADO,
  GMUD_INCLUSAO_TIPO,
  GMUD_TIPO_BLOQUEADO,
} from "./planejamento-controle.constants";
import { formatClassificacao, formatMilhoes } from "./planejamento-controle.format";
import type { LinhaPlanejamento, LinhaTestIds, NivelLinha } from "./types-planejamento-controle";

export type Alternados = ReadonlySet<string>;

const NIVEL: Record<NaturezaLinha, NivelLinha> = {
  CENTRO_CUSTO: "centro-custo",
  TIPO_OBRA: "tipo",
  ESCOPO_MACRO: "escopo",
  CATEGORIA: "categoria",
  FACILIDADES: "facilidades",
  TOTAL: "total",
};

const TAG: Record<EstadoLinha, string | null> = {
  NENHUM: null,
  PENDENTE_PROJECAO: null,
  SALDO_A_DESTINAR: null,
  AGUARDANDO_APROVACAO,
  CANCELAMENTO_AGUARDANDO_APROVACAO: EM_APROVACAO,
  TRANSFERENCIA_AGUARDANDO_APROVACAO: EM_APROVACAO,
  SEM_ESCOPO_MACRO,
};

function rotuloDoEstado(linha: LinhaPlanejamentoControle): string | null {
  return linha.estado === "CANCELAMENTO_AGUARDANDO_APROVACAO" && linha.natureza === "TIPO_OBRA"
    ? CANCELAMENTO_AGUARDANDO_APROVACAO_TIPO
    : TAG[linha.estado];
}

function tituloDoEstado(linha: LinhaPlanejamentoControle): string | undefined {
  switch (linha.estado) {
    case "CANCELAMENTO_AGUARDANDO_APROVACAO":
    case "TRANSFERENCIA_AGUARDANDO_APROVACAO":
      return linha.natureza === "TIPO_OBRA" ? GMUD_TIPO_BLOQUEADO() : GMUD_ESCOPO_BLOQUEADO();
    case "AGUARDANDO_APROVACAO":
      return linha.natureza === "TIPO_OBRA" ? GMUD_INCLUSAO_TIPO() : undefined;
    default:
      return undefined;
  }
}

function testIds(chave: string, entidadeId: string): LinhaTestIds {
  return {
    linha: `planejamento-linha-${chave}`,
    total: `planejamento-total-${chave}`,
    estado: `planejamento-estado-${chave}`,
    classificacao: `planejamento-classificacao-${chave}`,
    lupa: `planejamento-lupa-${entidadeId}`,
    menu: `planejamento-menu-${entidadeId}`,
  };
}

/** Um valor por competência da tabela; competência sem participação financeira fica "—" (não vira zero). */
function meses(competencias: readonly string[], totais: TotaisPlanejamento | null): string[] {
  return competencias.map((competencia) => {
    const mes = totais?.mensalizacao.find((item) => item.competencia === competencia);
    return mes ? formatMilhoes(mes.total) : NO_VALUE;
  });
}

function valores(
  { estado, totais }: LinhaPlanejamentoControle,
  competencias: readonly string[],
  semProjecao: boolean,
): Pick<LinhaPlanejamento, "total" | "nc" | "meses"> {
  if (semProjecao) return { total: NO_VALUE, nc: NO_VALUE, meses: meses(competencias, null) };
  if (totais) {
    return {
      total: formatMilhoes(totais.outlook.total),
      nc: formatMilhoes(totais.naoComprometido.total),
      meses: meses(competencias, totais),
    };
  }
  return {
    total: estado === "PENDENTE_PROJECAO" ? ESCOPO_PENDENTE : NO_VALUE,
    nc: estado === "PENDENTE_PROJECAO" ? "" : NO_VALUE,
    meses: meses(competencias, null),
  };
}

function formatar(
  linha: LinhaPlanejamentoControle,
  filhas: ReadonlySet<string>,
  semProjecao: ReadonlySet<string>,
  alternados: Alternados,
  competencias: readonly string[],
): LinhaPlanejamento {
  const expansivel = filhas.has(linha.chave);
  const entidadeId = linha.entidadeId ?? linha.chave;
  const escopo = linha.natureza === "ESCOPO_MACRO";
  return {
    key: linha.chave,
    testIds: testIds(linha.chave, entidadeId),
    nivel: NIVEL[linha.natureza],
    nome: linha.rotulo,
    classificacao: linha.classificacao ? formatClassificacao(linha.classificacao) : null,
    ...valores(linha, competencias, semProjecao.has(linha.chave)),
    expansivel,
    expandida: expansivel && alternados.has(linha.chave),
    estado: rotuloDoEstado(linha),
    estadoTitulo: tituloDoEstado(linha),
    entidadeId,
    menu: escopo ? "escopo" : linha.natureza === "TIPO_OBRA" ? "tipo" : null,
    comLupa: escopo,
  };
}

function chavesSemProjecao(linhas: readonly LinhaPlanejamentoControle[]): ReadonlySet<string> {
  const filhasDe = (chave: string, natureza: NaturezaLinha) =>
    linhas.filter((linha) => linha.paiChave === chave && linha.natureza === natureza);
  const tipoSemProjecao = (tipo: LinhaPlanejamentoControle) => {
    const escopos = filhasDe(tipo.chave, "ESCOPO_MACRO");
    return escopos.length > 0 && escopos.every((escopo) => escopo.totais === null);
  };
  const tipos = linhas.filter((linha) => linha.natureza === "TIPO_OBRA" && tipoSemProjecao(linha));
  const centros = linhas.filter((linha) => {
    if (linha.natureza !== "CENTRO_CUSTO") return false;
    const tiposDoCentro = filhasDe(linha.chave, "TIPO_OBRA");
    return tiposDoCentro.length > 0 && tiposDoCentro.every(tipoSemProjecao);
  });
  return new Set([...tipos, ...centros].map((linha) => linha.chave));
}

export function linhasVisiveis(
  linhas: readonly LinhaPlanejamentoControle[],
  competencias: readonly string[],
  alternados: Alternados,
): LinhaPlanejamento[] {
  const pais = new Map(linhas.map((linha) => [linha.chave, linha.paiChave]));
  const filhas = new Set(linhas.flatMap((linha) => (linha.paiChave ? [linha.paiChave] : [])));
  const visivel = (linha: LinhaPlanejamentoControle): boolean => {
    for (let pai = linha.paiChave; pai; pai = pais.get(pai) ?? null) {
      if (!alternados.has(pai)) return false;
    }
    return true;
  };

  const semProjecao = chavesSemProjecao(linhas);

  return linhas
    .filter(visivel)
    .map((linha) => formatar(linha, filhas, semProjecao, alternados, competencias));
}
